<script setup lang="ts">
import {User} from "@/types";
import {computed, ref, watch} from "vue";
import SendcryptLogo from "@/components/SendcryptLogo.vue";
import {useUserAvatar} from "@/composables/avatar";
import {InertiaForm} from "@inertiajs/vue3";

interface Form {
    user: User | null;
    group_id: number | null;
}

interface Props {
    // @ts-ignore
    modelValue: InertiaForm<Form>,
    isDialogOpen: boolean,
    isLoading: boolean,
    group: string | null,
    searchInput?: string,
    items: Array<User>,
    type?: string
    errors?: string | null,
}

const props = withDefaults(defineProps<Props>(), {
    type: 'group',
    searchInput: '',
    errors: null,
})
const emit = defineEmits(['update:modelValue', 'update:searchInput', 'update:isDialogOpen', 'click:confirm'])
const value = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})
const search = computed({
    get() {
        return props.searchInput
    },
    set(value) {
        emit('update:searchInput', value)
    }
})
const isOpen = computed({
    get() {
        return props.isDialogOpen
    },
    set(value) {
        emit('update:isDialogOpen', value)
    }
})
const isFocused = ref(false)

watch(isOpen, (val) => {
    if (val) {
        search.value = ''
        value.value.user = null
    }
}, {immediate: true})
</script>

<template>
    <v-dialog
        v-model="isOpen"
        width="700"
    >
        <v-card
            :flat="true"
            rounded="lg"
            elevation="15"
        >
            <v-card-title class="mt-3">
                <v-btn
                    variant="plain"
                    icon="mdi-close"
                    style="right: 0; position: absolute;"
                    class="mr-3"
                    size="small"
                    @click="isOpen = false"
                />
            </v-card-title>
            <v-card-text class="pb-0">
                <div class="text-center">
                    <sendcrypt-logo />
                    <p class="text-h6 black--text font-weight-regular">
                        Assign collaborator to group <span
                            class="font-weight-bold"
                        >{{ group }}</span>
                    </p>
                </div>
                <v-row>
                    <v-col
                        cols="12"
                        class="pb-0"
                    >
                        <v-alert
                            v-if="errors"
                            type="error"
                            class="pa-5"
                            dense
                            outlined
                            variant="text"
                        >
                            <div class="black--text">
                                {{ errors }}
                            </div>
                        </v-alert>
                        <v-autocomplete
                            v-if="!value.user"
                            v-model="value.user"
                            v-model:search="search"
                            variant="outlined"
                            rounded="lg"
                            color="blue"
                            class="my-3"
                            :bg-color=" isFocused ? 'white' :'grey-lighten-4'"
                            placeholder="Search by full name, username or email"
                            prepend-inner-icon="mdi-magnify"
                            :items="items"
                            :loading="isLoading"
                            :hide-no-data="search === null || search.length < 1"
                            :no-filter="true"
                            :return-object="true"
                            density="compact"
                            :hide-details="true"
                            @focus="isFocused = true"
                            @blur="isFocused = false"
                        >
                            <template #no-data>
                                <v-list-item>
                                    <v-list-item-title class="text-body-2 font-weight-bold">
                                        Could not find a member account matching {{ search }}
                                    </v-list-item-title>
                                </v-list-item>
                            </template>
                            <template #item="{ props: attrs, item }">
                                <v-list-item
                                    v-bind="attrs"
                                    :title="item.raw.name"
                                >
                                    <template #prepend>
                                        <v-avatar
                                            size="24"
                                            rounded="xl"
                                        >
                                            <v-img
                                                :src="useUserAvatar(item.raw.name)"
                                            />
                                        </v-avatar>
                                    </template>
                                    <v-list-item-subtitle class="font-weight-regular">
                                        {{ item.raw.email }} · {{ item.raw.full_name }}
                                    </v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <v-list-item
                            v-if="value.user"
                            class="bg-light-blue-lighten-5 rounded blue-outlined py-3 mt-10"
                            density="comfortable"
                        >
                            <template #prepend>
                                <v-avatar
                                    size="36"
                                    rounded="xl"
                                    class="border-sm"
                                >
                                    <v-img
                                        :src="useUserAvatar(value.user.name)"
                                        alt="avatar"
                                    />
                                </v-avatar>
                            </template>
                            <v-list-item-title class="font-weight-bold text-blue-darken-2">
                                {{ value.user.name }}
                            </v-list-item-title>
                            <v-list-item-title class="text-body-2 font-weight-regular text-blue-darken-2">
                                {{ value.user.email }} · {{ value.user.full_name }}
                            </v-list-item-title>
                            <template #append>
                                <v-btn
                                    variant="plain"
                                    size="x-small"
                                    icon="mdi-close"
                                    color="blue-darken-2"
                                    @click="value.user = null"
                                />
                            </template>
                        </v-list-item>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-6">
                <submit-btn
                    :disabled="!value.user"
                    type="submit"
                    depressed
                    class="my-5 text-none text-body-2 font-weight-medium"
                    :block="true"
                    color="success"
                    @click="$emit('click:confirm', value)"
                >
                    {{ !value.user ? 'Select a collaborator above' : `Assign ${value.user.name} to this ${type}` }}
                </submit-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.blue-outlined {
    border: 1px solid lightskyblue;
}
</style>
